import { differenceInDays } from 'date-fns';

export interface ZoneWindow {
  month: number;
  dayStart: number;
  dayEnd: number;
  endMonth?: number;
  endDay?: number;
}

export interface ZoneConfig {
  name: string;
  states: string[];
  fallBrassicaWindow: ZoneWindow;
  fallCloverWindow: ZoneWindow;
  frostSeedWindow: ZoneWindow;
  averageFirstFrostMonth: number;
  averageFirstFrostDay: number;
  averageLastFrostMonth: number;
  averageLastFrostDay: number;
  rutPeakMonth: number;
  rutPeakDay: number;
}

export const ZONE_CONFIG: Record<string, ZoneConfig> = {
  northern_midwest: {
    name: 'Northern Midwest',
    // WI, MI, MN, IA — WTI Zones 7N, 8, 9, 10
    states: ['WI', 'MI', 'MN', 'IA'],
    fallBrassicaWindow: { month: 8, dayStart: 1, dayEnd: 31, endMonth: 9, endDay: 1 },
    fallCloverWindow: { month: 7, dayStart: 15, dayEnd: 31, endMonth: 9, endDay: 5 },
    frostSeedWindow: { month: 2, dayStart: 20, dayEnd: 28 },
    averageFirstFrostMonth: 10,
    averageFirstFrostDay: 15,
    averageLastFrostMonth: 4,
    averageLastFrostDay: 15,
    rutPeakMonth: 11,
    rutPeakDay: 10,
  },
  mid_midwest_south: {
    name: 'Mid-Midwest / South',
    // MO, KS, KY, TN, VA, IN, OH — WTI Zones 4, 5, 7S
    states: ['MO', 'KS', 'KY', 'TN', 'VA', 'IN', 'OH'],
    fallBrassicaWindow: { month: 8, dayStart: 15, dayEnd: 31, endMonth: 9, endDay: 15 },
    fallCloverWindow: { month: 8, dayStart: 1, dayEnd: 31, endMonth: 10, endDay: 15 },
    frostSeedWindow: { month: 2, dayStart: 1, dayEnd: 28 },
    averageFirstFrostMonth: 11,
    averageFirstFrostDay: 1,
    averageLastFrostMonth: 3,
    averageLastFrostDay: 20,
    rutPeakMonth: 11,
    rutPeakDay: 5,
  },
  deep_south: {
    name: 'Deep South',
    // AL, MS, GA, FL, TX — WTI Zones 6, 11–16
    states: ['AL', 'MS', 'GA', 'FL', 'TX'],
    fallBrassicaWindow: { month: 9, dayStart: 15, dayEnd: 30, endMonth: 11, endDay: 1 },
    fallCloverWindow: { month: 9, dayStart: 1, dayEnd: 30, endMonth: 11, endDay: 15 },
    frostSeedWindow: { month: 1, dayStart: 15, dayEnd: 31 },
    averageFirstFrostMonth: 11,
    averageFirstFrostDay: 15,
    averageLastFrostMonth: 2,
    averageLastFrostDay: 15,
    rutPeakMonth: 11,
    rutPeakDay: 1,
  },
};

// Unknown states default to Northern Midwest (WI is the primary target user)
const DEFAULT_ZONE = 'northern_midwest';

const STATE_TO_ZONE: Record<string, string> = {
  WI: 'northern_midwest',
  MI: 'northern_midwest',
  MN: 'northern_midwest',
  IA: 'northern_midwest',
  MO: 'mid_midwest_south',
  KS: 'mid_midwest_south',
  KY: 'mid_midwest_south',
  TN: 'mid_midwest_south',
  VA: 'mid_midwest_south',
  IN: 'mid_midwest_south',
  OH: 'mid_midwest_south',
  AL: 'deep_south',
  MS: 'deep_south',
  GA: 'deep_south',
  FL: 'deep_south',
  TX: 'deep_south',
};

export function getZoneConfig(zone: string): ZoneConfig | null {
  return ZONE_CONFIG[zone] ?? null;
}

// M231 (issue #1318): which of the three agronomic windows a zoneAdjustable calendar task
// should use for its zone offset, selected by proximity to the task's own calendarMonth
// rather than always defaulting to fallBrassicaWindow.
export type ZoneWindowKey = 'frostSeedWindow' | 'fallCloverWindow' | 'fallBrassicaWindow';

const ZONE_WINDOW_KEYS: ZoneWindowKey[] = ['frostSeedWindow', 'fallCloverWindow', 'fallBrassicaWindow'];

function circularMonthDistance(a: number, b: number): number {
  const diff = Math.abs(a - b) % 12;
  return Math.min(diff, 12 - diff);
}

/**
 * Selects which of the three agronomic windows (frost-seed, fall-clover, fall-brassica) best
 * represents a zone-adjustable calendar task, based on how close the task's own calendarMonth
 * is to each window's Northern Midwest reference month. Ties break toward the earlier key in
 * ZONE_WINDOW_KEYS (frostSeed < fallClover < fallBrassica) — no current template task produces
 * an exact tie (see M231 plan enumeration), but the tie-break is deterministic if one ever does.
 */
export function selectZoneWindowKey(calendarMonth: number): ZoneWindowKey {
  const northern = ZONE_CONFIG.northern_midwest;
  let best: ZoneWindowKey = ZONE_WINDOW_KEYS[0];
  let bestDistance = Infinity;
  for (const key of ZONE_WINDOW_KEYS) {
    const distance = circularMonthDistance(calendarMonth, northern[key].month);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = key;
    }
  }
  return best;
}

export function getZoneForState(stateCode: string): string {
  return STATE_TO_ZONE[stateCode.toUpperCase()] ?? DEFAULT_ZONE;
}

/**
 * Compute the day offset between a target zone's window start and Northern Midwest's window start.
 * Used by the checklist engine to shift calendar-date tasks by zone.
 * A reference year is used so month arithmetic stays correct.
 */
export function computeZoneWindowOffset(
  northernWindow: ZoneWindow,
  targetWindow: ZoneWindow,
  referenceYear = 2000,
): number {
  const northernStart = new Date(referenceYear, northernWindow.month - 1, northernWindow.dayStart);
  const targetStart = new Date(referenceYear, targetWindow.month - 1, targetWindow.dayStart);
  return differenceInDays(targetStart, northernStart);
}
